import { useCallback, useEffect, useRef, useState } from 'react';

export type IntersectionRoot = Element | Document | React.RefObject<Element | null> | null;

export type IntersectionTarget<T extends Element> = React.RefObject<T | null> | T | null;

export type IntersectionRefCallback<T extends Element> = (node: T | null) => void;

export interface UseIntersectionObserverOptions<T extends Element = Element> {
  target?: IntersectionTarget<T>;
  root?: IntersectionRoot;
  rootMargin?: string;
  threshold?: number | number[];
  enabled?: boolean;
  once?: boolean;
  resetKey?: unknown;
  onChange?: (entry: IntersectionObserverEntry) => void;
  onEnter?: (entry: IntersectionObserverEntry) => void;
  onLeave?: (entry: IntersectionObserverEntry) => void;
}

export interface UseIntersectionObserverResult<T extends Element = Element> {
  ref: IntersectionRefCallback<T>;
}

const isRefObject = (value: unknown): value is React.RefObject<unknown> =>
  !!value && typeof value === 'object' && 'current' in value;

const resolveTarget = <T extends Element>(target: IntersectionTarget<T> | undefined): T | null => {
  if (!target) return null;
  if (isRefObject(target)) return (target.current as T | null) ?? null;
  return target;
};

const resolveRoot = (root: IntersectionRoot | undefined): Element | Document | null => {
  if (!root) return null;
  if (isRefObject(root)) return (root.current as Element | null) ?? null;
  return root;
};

const useObserve = <T extends Element>(
  options: UseIntersectionObserverOptions<T>
): IntersectionRefCallback<T> => {
  const {
    target,
    root,
    rootMargin = '0px',
    threshold = 0,
    enabled = true,
    once = false,
    resetKey,
  } = options;

  const [node, setNode] = useState<T | null>(null);
  const setRef = useCallback((next: T | null) => setNode(next), []);

  const latest = useRef(options);
  latest.current = options;

  const thresholdKey = Array.isArray(threshold) ? threshold.join(',') : String(threshold);

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === 'undefined') return undefined;
    const element = resolveTarget(target) ?? node;
    if (!element) return undefined;

    let latched = false;
    const observer = new IntersectionObserver(
      entries => {
        if (latched) return;
        const entry = entries[0];
        if (!entry) return;
        const handlers = latest.current;
        handlers.onChange?.(entry);
        if (entry.isIntersecting) handlers.onEnter?.(entry);
        else handlers.onLeave?.(entry);
        if (entry.isIntersecting && once) {
          latched = true;
          observer.disconnect();
        }
      },
      { root: resolveRoot(root), rootMargin, threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, target, root, enabled, once, rootMargin, thresholdKey, resetKey]);

  return setRef;
};

export function useIntersectionObserver<T extends Element = Element>(
  options?: UseIntersectionObserverOptions<T>
): UseIntersectionObserverResult<T>;
/**
 * @deprecated Positional infinite-scroll form. Prefer the options object:
 * `useIntersectionObserver({ target, root, onEnter, enabled })`.
 */
export function useIntersectionObserver(
  containerRef: React.RefObject<HTMLElement | null>,
  lastElementRef: React.RefObject<HTMLElement | null>,
  onIntersection: () => void,
  hasNext: boolean,
  isLoading: boolean,
  mode?: 'entering' | 'leaving' | string,
  rootMargin?: string,
  threshold?: number,
  resetKey?: string | number
): void;
export function useIntersectionObserver<T extends Element = Element>(
  arg1?: UseIntersectionObserverOptions<T> | React.RefObject<HTMLElement | null>,
  lastElementRef?: React.RefObject<HTMLElement | null>,
  onIntersection?: () => void,
  hasNext = false,
  isLoading = false,
  mode: 'entering' | 'leaving' | string = 'entering',
  rootMargin = '10px',
  threshold = 0.1,
  resetKey?: string | number
): UseIntersectionObserverResult<T> | void {
  const legacy = isRefObject(arg1);

  const gatedTrigger = (): void => {
    if (hasNext && !isLoading) onIntersection?.();
  };

  const options: UseIntersectionObserverOptions<T> = legacy
    ? {
        target: lastElementRef as IntersectionTarget<T>,
        root: arg1 as React.RefObject<HTMLElement | null>,
        rootMargin,
        threshold,
        resetKey: `${hasNext}|${isLoading}|${resetKey ?? ''}`,
        onEnter: mode === 'leaving' ? undefined : gatedTrigger,
        onLeave: mode === 'leaving' ? gatedTrigger : undefined,
      }
    : ((arg1 as UseIntersectionObserverOptions<T>) ?? {});

  const ref = useObserve(options);
  return legacy ? undefined : { ref };
}
